import { Button } from "@/components/ui/button";
import { Play, Loader2, ShieldAlert, CheckCircle2, Check, Users } from "lucide-react";
import FeedbackButtons, { LEAD_REASONS, SUGGESTION_REASONS } from "@/components/FeedbackButtons";

const CTA_COPY = {
  strategy: { title: "Execute This Strategy", body: "ISM will search for prospects matching this strategy and add leads to your pipeline", busy: "Finding Prospects...", action: "Find Prospects" },
  research: { title: "Act on This Intel", body: "ISM will find contacts at this company and generate outreach based on this intel", busy: "Researching...", action: "Find Contacts" },
  competitor: { title: "Counter This Threat", body: "ISM will research this competitor and find prospects they may be targeting", busy: "Analyzing...", action: "Research Competitor" },
};

export const ExecuteStrategyCTA = ({ type, executing, enabled, onExecute }) => {
  const copy = CTA_COPY[type] || CTA_COPY.competitor;
  return (
    <div className="bg-gradient-to-r from-[#00A38A]/20 to-[text-ism-teal]/20 rounded-lg p-4 border border-[#00A38A]/30">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-white font-semibold flex items-center gap-2"><Play className="h-5 w-5 text-[#00A38A]" />{copy.title}</h4>
          <p className="text-gray-400 text-sm mt-1">{copy.body}</p>
        </div>
        <Button onClick={onExecute} disabled={executing || !enabled}
          className={`px-6 ${enabled ? "bg-[#00A38A] hover:bg-[#008570] text-white" : "bg-gray-700 text-gray-400 cursor-not-allowed"}`} data-testid="execute-strategy-btn">
          {executing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{copy.busy}</> : <><Play className="h-4 w-4 mr-2" />{copy.action}</>}
        </Button>
      </div>
      {!enabled && (
        <p className="text-xs text-amber-400/70 mt-3 flex items-center gap-1"><ShieldAlert className="h-3 w-3" />Click on any section above to review before executing</p>
      )}
    </div>
  );
};

const TargetingApplied = ({ targeting }) => {
  const gates = targeting?.gates || {};
  const excluded = gates.excludedByFeedback > 0;
  const avoiding = gates.avoidTitles?.length > 0;
  if (!targeting?.briefText) return null;
  return (
    <div className="mb-4 rounded-lg border border-ism-teal/30 bg-ism-teal/5 px-3 py-2" data-testid="execution-targeting">
      <p className="text-[10px] uppercase tracking-wider text-ism-teal/80 mb-0.5">Targeting applied from this action</p>
      <p className="text-sm text-gray-200" data-testid="execution-targeting-brief">{targeting.briefText}</p>
      {(excluded || avoiding) && (
        <p className="text-[11px] text-gray-400 mt-1" data-testid="execution-targeting-learned">
          Applied your feedback: {excluded && `${gates.excludedByFeedback} firm(s) excluded`}{excluded && avoiding && " · "}{avoiding && `avoiding ${gates.avoidTitles.join(", ")}`}
        </p>
      )}
    </div>
  );
};

const GeneratedLead = ({ lead, idx, userId, actionId }) => (
  <div className="bg-white/5 rounded p-2 text-sm flex items-start justify-between gap-2" data-testid={`execution-lead-${idx}`}>
    <div className="min-w-0">
      <span className="text-white font-medium">{lead.name}</span>
      {lead.title && <span className="text-gray-400"> - {lead.title}</span>}
      {lead.company && <span className="text-ism-teal"> at {lead.company}</span>}
      {(lead.grade || lead.employeeCount || lead.industry) && (
        <p className="text-[11px] text-gray-500 mt-0.5">
          {[lead.grade && `Grade ${lead.grade}`, lead.industry, lead.employeeCount && `${lead.employeeCount.toLocaleString()} employees`].filter(Boolean).join(" · ")}
        </p>
      )}
    </div>
    <FeedbackButtons
      userId={userId}
      agentType="strategy_execution"
      actionId={actionId}
      outputSummary={`${lead.name}${lead.title ? ` - ${lead.title}` : ""}${lead.company ? ` at ${lead.company}` : ""}`}
      context={{ kind: "lead", leadId: lead.leadId, companyName: lead.company, personTitle: lead.title, sources: (lead.sources || []).filter((s) => typeof s === "string") }}
      reasons={LEAD_REASONS}
      testIdPrefix={`execution-lead-${idx}`}
    />
  </div>
);

export const StrategyExecutedPanel = ({ results, activityData, userId, onReset }) => (
  <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
    <div className="flex items-center justify-between gap-2 mb-4">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-green-400" />
        <h4 className="text-green-400 font-semibold">Strategy Executed</h4>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-500">Was this useful?</span>
        <FeedbackButtons
          userId={userId}
          agentType="strategy_execution"
          actionId={results.actionId}
          outputSummary={results.targeting?.briefText || activityData?.summary}
          context={{ kind: "execution", type: activityData?.type, leadsGenerated: results.leadsGenerated?.length || 0 }}
          reasons={SUGGESTION_REASONS}
          testIdPrefix="execution"
        />
      </div>
    </div>
    <TargetingApplied targeting={results.targeting} />

    {results.actionsTaken?.length > 0 && (
      <div className="mb-4">
        <p className="text-gray-400 text-xs mb-2">Actions Taken</p>
        <ul className="space-y-2">
          {results.actionsTaken.map((action, idx) => (
            <li key={typeof action === "string" ? action : action.id || action.title || idx} className="flex items-start gap-2 text-gray-300 text-sm">
              <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />{action}
            </li>
          ))}
        </ul>
      </div>
    )}

    {results.leadsGenerated?.length > 0 && (
      <div className="mb-4">
        <p className="text-gray-400 text-xs mb-2 flex items-center gap-2"><Users className="h-4 w-4" />Leads Generated ({results.leadsGenerated.length})</p>
        <div className="space-y-2">
          {results.leadsGenerated.map((lead, idx) => <GeneratedLead key={lead.leadId || `${lead.name}-${idx}`} lead={lead} idx={idx} userId={userId} actionId={results.actionId} />)}
        </div>
      </div>
    )}

    {results.nextSteps && (
      <div className="mt-4 pt-4 border-t border-green-500/20">
        <p className="text-gray-400 text-xs mb-2">Recommended Next Steps</p>
        <p className="text-gray-300 text-sm">{results.nextSteps}</p>
      </div>
    )}

    <Button onClick={onReset} variant="outline" size="sm" className="mt-4 border-green-500/30 text-green-400 hover:bg-green-500/10">
      Execute Another Strategy
    </Button>
  </div>
);
